import {
  ImgHTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

import { cn } from "../utls/utils";
import ThemedSpinner, { SpinnerSize } from "./spinner";
import Typography from "./typography";

type LoadingState = "loading" | "loaded" | "error";

export interface LoadingImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Custom node rendered while the image loads. */
  fallback?: ReactNode;
  /** Node rendered if the image fails to load. */
  errorFallback?: ReactNode;
  /** Optional wrapper class for the outer container. */
  wrapperClassName?: string;
  /** Size used for the default spinner fallback. */
  spinnerSize?: SpinnerSize;
}

const defaultLoading = (size: SpinnerSize) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-lg border border-beige-300 bg-cream-50 p-4 text-brown-700">
    <ThemedSpinner size={size} />
    <Typography variant="caption" className="text-brown-600">
      Loading image...
    </Typography>
  </div>
);

const defaultError = (
  <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-danger/40 bg-danger/5 p-4 text-danger">
    <Typography variant="caption" className="font-medium">
      Image failed to load
    </Typography>
  </div>
);

/**
 * Displays an image with Harper-themed loading and error fallbacks.
 */
export function LoadingImage({
  fallback,
  errorFallback,
  wrapperClassName,
  className,
  src,
  spinnerSize = "sm",
  onLoad,
  onError,
  ...imgProps
}: LoadingImageProps) {
  const [status, setStatus] = useState<LoadingState>(() =>
    src ? "loading" : "error"
  );

  useEffect(() => {
    setStatus(src ? "loading" : "error");
  }, [src]);

  const loadingNode = useMemo(
    () => fallback ?? defaultLoading(spinnerSize),
    [fallback, spinnerSize]
  );
  const errorNode = useMemo(
    () => errorFallback ?? defaultError,
    [errorFallback]
  );

  return (
    <div
      className={cn(
        "relative flex h-full w-full items-center justify-center overflow-hidden rounded-lg",
        wrapperClassName
      )}
      data-testid="loading-image"
    >
      <img
        {...imgProps}
        src={src}
        alt={imgProps?.alt ?? ""}
        onLoad={(event) => {
          setStatus("loaded");
          onLoad?.(event);
        }}
        onError={(event) => {
          setStatus("error");
          onError?.(event);
        }}
        className={cn(
          "h-full w-full object-cover transition-opacity duration-300",
          status === "loaded" ? "opacity-100" : "opacity-0",
          className
        )}
        aria-busy={status === "loading"}
      />

      {status !== "loaded" ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          {status === "loading" ? loadingNode : errorNode}
        </div>
      ) : null}
    </div>
  );
}

export default LoadingImage;
